import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Plus, ChevronLeft, ChevronRight, Clock, MapPin, EyeOff, Gamepad2, Search, X } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth, isRespAdmin } from "@/context/AuthContext";
import { BottomSheet, DeleteButton, Field, toISO, GIORNI_SETTIMANA } from "@/components/Shared";
import { useRefreshOnPull } from "@/components/PullToRefresh";
import { Switch } from "@/components/ui/switch";

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

export default function Calendario() {
  const { user } = useAuth();
  const canEdit = isRespAdmin(user);
  const oggi = new Date();
  const [anno, setAnno] = useState(oggi.getFullYear());
  const [mese, setMese] = useState(oggi.getMonth());
  const [attivita, setAttivita] = useState([]);
  const [giornoSel, setGiornoSel] = useState(toISO(oggi));
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(() => {
    const da = `${anno}-${String(mese + 1).padStart(2, "0")}-01`;
    const a = `${anno}-${String(mese + 1).padStart(2, "0")}-31`;
    api.get(`/attivita?da=${da}&a=${a}`).then((r) => setAttivita(r.data));
  }, [anno, mese]);

  useEffect(() => { load(); }, [load]);
  useRefreshOnPull(load);

  const perGiorno = useMemo(() => {
    const m = {};
    attivita.forEach((a) => { (m[a.data] = m[a.data] || []).push(a); });
    return m;
  }, [attivita]);

  const cambiaMese = (delta) => {
    let m = mese + delta, y = anno;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    setMese(m); setAnno(y);
  };

  const startIdx = (new Date(anno, mese, 1).getDay() + 6) % 7;
  const giorniNelMese = new Date(anno, mese + 1, 0).getDate();
  const celle = [...Array(startIdx).fill(null), ...Array.from({ length: giorniNelMese }, (_, i) => i + 1)];

  const apri = (a = null) => {
    setEditId(a?.id || null);
    setForm(a
      ? { titolo: a.titolo, data: a.data, ora_inizio: a.ora_inizio, ora_fine: a.ora_fine, luogo: a.luogo || "", note: a.note || "", visibile_animatori: a.visibile_animatori !== false, visibile_genitori: !!a.visibile_genitori }
      : { titolo: "", data: giornoSel, ora_inizio: "09:00", ora_fine: "12:00", luogo: "", note: "", visibile_animatori: true, visibile_genitori: false });
    setSheetOpen(true);
  };

  const salva = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, luogo: form.luogo || null, note: form.note || null };
    try {
      if (editId) await api.put(`/attivita/${editId}`, payload);
      else await api.post("/attivita", payload);
      toast.success("Attività salvata");
      setSheetOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const elimina = async () => {
    try {
      await api.delete(`/attivita/${editId}`);
      toast.success("Attività eliminata");
      setSheetOpen(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const delGiorno = perGiorno[giornoSel] || [];

  const q = search.trim().toLowerCase();
  const risultati = q
    ? attivita.filter((a) =>
        `${a.titolo} ${a.luogo || ""} ${a.note || ""} ${a.gioco_nome || ""}`.toLowerCase().includes(q)
      )
    : [];

  return (
    <div data-testid="calendario-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900">Calendario</h1>
          <p className="text-sm text-slate-500 font-semibold">Attività e promemoria</p>
        </div>
        {canEdit && (
          <button className="btn-primary" onClick={() => apri()} data-testid="nuova-attivita-btn">
            <Plus className="h-4 w-4" /> Nuova attività
          </button>
        )}
      </div>

      <div className="relative mb-5">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input type="search" className="input-app !pl-11 !pr-10" placeholder="Cerca attività nel mese in corso (titolo, luogo, note, gioco)..." value={search} onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }} onChange={(e) => setSearch(e.target.value)} data-testid="calendario-search-input" />
        {q && (
          <button type="button" onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="calendario-search-clear" aria-label="Pulisci ricerca">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {q ? (
        <div className="space-y-3" data-testid="calendario-risultati-ricerca">
          <p className="text-xs text-slate-400 font-bold uppercase tracking-wide">{risultati.length} risultati per {`"${search}"`}</p>
          {risultati.length === 0 ? (
            <div className="card-app p-6 text-center">
              <p className="text-sm text-slate-400 font-semibold">Nessuna attività trovata in {MESI[mese]} {anno}</p>
            </div>
          ) : (
            risultati.map((a) => (
              <button key={a.id} disabled={!canEdit} onClick={() => canEdit && apri(a)}
                className="card-app p-4 w-full text-left hover:shadow-md transition-shadow disabled:cursor-default"
                data-testid={`attivita-risultato-${a.id}`}>
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="badge-info shrink-0">{a.data.split("-").reverse().join("/")}</span>
                  <p className="font-display font-extrabold text-slate-900 flex-1">{a.titolo}</p>
                  {!a.visibile_animatori && <span className="badge-neg inline-flex items-center gap-1"><EyeOff className="h-3 w-3" /> Riservata</span>}
                </div>
                <p className="text-xs text-slate-500 font-semibold flex items-center gap-1"><Clock className="h-3 w-3" /> {a.ora_inizio} – {a.ora_fine}</p>
                {a.luogo && <p className="text-xs text-slate-400 font-semibold flex items-center gap-1 mt-0.5"><MapPin className="h-3 w-3" /> {a.luogo}</p>}
                {a.gioco_nome && <p className="text-xs text-sky-600 font-bold flex items-center gap-1 mt-0.5"><Gamepad2 className="h-3 w-3" /> {a.gioco_nome}</p>}
                {a.note && <p className="text-xs text-slate-400 mt-1">{a.note}</p>}
              </button>
            ))
          )}
        </div>
      ) : (
      <div className="grid lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 card-app p-5">
          <div className="flex items-center justify-between mb-4">
            <button className="btn-ghost !p-2" onClick={() => cambiaMese(-1)} data-testid="mese-prev-btn"><ChevronLeft className="h-4 w-4" /></button>
            <p className="font-display font-extrabold text-slate-900">{MESI[mese]} {anno}</p>
            <button className="btn-ghost !p-2" onClick={() => cambiaMese(1)} data-testid="mese-next-btn"><ChevronRight className="h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 mb-1">
            {GIORNI_SETTIMANA.map((g) => <p key={g} className="text-[10px] font-extrabold text-slate-400 text-center uppercase">{g}</p>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {celle.map((d, i) => {
              if (!d) return <div key={`x${i}`} />;
              const iso = `${anno}-${String(mese + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
              const has = perGiorno[iso]?.length > 0;
              const isSel = iso === giornoSel;
              const isOggi = iso === toISO(oggi);
              return (
                <button key={iso} onClick={() => setGiornoSel(iso)}
                  className={`aspect-square min-w-0 rounded-2xl text-sm font-bold flex flex-col items-center justify-center transition-all ${
                    isSel ? "bg-amber-500 text-white shadow-md scale-105" : isOggi ? "bg-amber-100 text-amber-700" : "text-slate-600 hover:bg-slate-50"
                  }`}
                  data-testid={`giorno-${iso}`}>
                  {d}
                  {has && <span className={`h-1.5 w-1.5 rounded-full mt-0.5 ${isSel ? "bg-white" : "bg-sky-500"}`} />}
                </button>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-2">
          <p className="label-app">{giornoSel.split("-").reverse().join("/")}</p>
          {delGiorno.length === 0 ? (
            <div className="card-app p-6 text-center">
              <p className="text-sm text-slate-400 font-semibold">Nessuna attività in questo giorno</p>
            </div>
          ) : (
            <div className="space-y-3">
              {delGiorno.map((a) => (
                <button key={a.id} disabled={!canEdit} onClick={() => canEdit && apri(a)}
                  className="card-app p-4 w-full text-left hover:shadow-md transition-shadow disabled:cursor-default"
                  data-testid={`attivita-card-${a.id}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className="font-display font-extrabold text-slate-900 flex-1">{a.titolo}</p>
                    {!a.visibile_animatori && <span className="badge-neg inline-flex items-center gap-1"><EyeOff className="h-3 w-3" /> Riservata</span>}
                  </div>
                  <p className="text-xs text-slate-500 font-semibold flex items-center gap-1"><Clock className="h-3 w-3" /> {a.ora_inizio} – {a.ora_fine}</p>
                  {a.luogo && <p className="text-xs text-slate-400 font-semibold flex items-center gap-1 mt-0.5"><MapPin className="h-3 w-3" /> {a.luogo}</p>}
                  {a.gioco_nome && <p className="text-xs text-sky-600 font-bold flex items-center gap-1 mt-0.5"><Gamepad2 className="h-3 w-3" /> {a.gioco_nome}</p>}
                  {a.note && <p className="text-xs text-slate-400 mt-1">{a.note}</p>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      <BottomSheet open={sheetOpen} onOpenChange={setSheetOpen} title={editId ? "Modifica attività" : "Nuova attività"} testId="attivita-sheet">
        {form && (
          <form onSubmit={salva}>
            <Field label="Titolo"><input className="input-app" required value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} data-testid="attivita-titolo-input" /></Field>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3">
              <div className="col-span-2 sm:col-span-1 min-w-0"><Field label="Data"><input type="date" className="input-app min-w-0" required value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} data-testid="attivita-data-input" /></Field></div>
              <div className="min-w-0"><Field label="Inizio"><input type="time" className="input-app min-w-0" required value={form.ora_inizio} onChange={(e) => setForm({ ...form, ora_inizio: e.target.value })} data-testid="attivita-inizio-input" /></Field></div>
              <div className="min-w-0"><Field label="Fine"><input type="time" className="input-app min-w-0" required value={form.ora_fine} onChange={(e) => setForm({ ...form, ora_fine: e.target.value })} data-testid="attivita-fine-input" /></Field></div>
            </div>
            <Field label="Luogo (facoltativo)"><input className="input-app" value={form.luogo} onChange={(e) => setForm({ ...form, luogo: e.target.value })} data-testid="attivita-luogo-input" /></Field>
            <Field label="Note (facoltative)"><textarea className="input-app" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="attivita-note-input" /></Field>
            <div className="flex items-center justify-between rounded-2xl bg-slate-50 p-4 mb-4">
              <div>
                <p className="text-sm font-bold text-slate-700">Visibile ad Animatori e Aiuti</p>
                <p className="text-xs text-slate-400">Se disattivato, la vedono solo Responsabili e Amministratore</p>
              </div>
              <Switch checked={form.visibile_animatori} onCheckedChange={(v) => setForm({ ...form, visibile_animatori: v })} data-testid="attivita-visibile-switch" />
            </div>
            <div className="flex items-center justify-between rounded-2xl bg-sky-50 p-4 mb-4">
              <div>
                <p className="text-sm font-bold text-slate-700">Visibile ai Genitori</p>
                <p className="text-xs text-slate-400">Compare nel calendario e nel programma dell'area famiglia</p>
              </div>
              <Switch checked={form.visibile_genitori} onCheckedChange={(v) => setForm({ ...form, visibile_genitori: v })} data-testid="attivita-visibile-genitori-switch" />
            </div>
            <button className="btn-primary w-full" disabled={saving} data-testid="attivita-submit-btn">{saving ? "Salvataggio..." : "Salva attività"}</button>
            {editId && (
              <div className="mt-3 flex justify-end">
                <DeleteButton onConfirm={elimina} entity="questa attività" testId="attivita-delete-btn" />
              </div>
            )}
          </form>
        )}
      </BottomSheet>
    </div>
  );
}
